import {Assert, Range, SleepAsync} from "js-vextensions";
import {AutoRun_HandleBail} from "mobx-graphlink";
import {EffectPointerPlayer} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";
import {EstimateJournalEntryStartTime} from "../../../Server/DB/@Shared/JournalTimeEstimates.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";
import {EngineConfig_ConceptLink} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_ConceptLink.js";
import {DreamQuiz_QuestionType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_DreamQuiz.js";
import {GetDreamEvents, GetSegmentToDreamMap, GetValidQuizSegments} from "../../../Server/DB/journalEntries.js";
import {JournalSegment} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {MeID} from "../../../Server/DB/users.js";
import {GetAudioFilesWithSubpath} from "../../../Store/main/general/CacheState.js";
import {GetAnchorStatDayOffset} from "../../../UI/Stats/Helpers.js";
import {PlaySound, PlaySound_StopAll} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {AudioFileEntry} from "../../../Utils/Bridge/Bridge_Native/MediaTypes.js";
import {NarrateText_ForEngineComp, NarrateText_ForEngineComp_OptInit} from "../../../Utils/Services/TTS.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {AlarmsComp} from "./AlarmsComp.js";
import {FindFreshRange} from "./DreamQuiz/DreamQuizSubcomp.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {RealityCheckComp} from "./RealityCheckComp.js";

export class ConceptLinkComp extends EngineSessionComp<EngineConfig_ConceptLink> {
	constructor(session: LiveSession, config: EngineConfig_ConceptLink) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());

		this.dbAutorun_disposer = AutoRun_HandleBail(()=>{
			this.validSegmentsForSequences_raw = GetValidQuizSegments(this.c.lucidSegmentsOnly, this.c.segmentMinEvents, this.c.eventMaxWords, DreamQuiz_QuestionType.isPresent);
			const segmentToDreamMap = GetSegmentToDreamMap(MeID());
			this.validSegmentsForSequences_withBoosts = this.validSegmentsForSequences_raw.SelectMany(segment=>{
				const dream = segmentToDreamMap.get(segment);
				Assert(dream, "Failed to find dream for segment.");
				const dreamTime = EstimateJournalEntryStartTime(dream);
				const statDayOffset = GetAnchorStatDayOffset(dreamTime, "night");
				
				// apply pick-weight multipliers (keyed by recency; highest multiplier wins)
				const multipliersCoveringRange = Object.entries(this.c.recencyBoosts ?? {})
					.filter(([recencyStr, _multiplier])=>Math.abs(statDayOffset) <= parseInt(recencyStr))
					.map(a=>a[1]);
				const multiplier = multipliersCoveringRange.length ? Math.max(...multipliersCoveringRange) : 1;

				const copiesOfSegment = [] as JournalSegment[];
				for (let i = 0; i < multiplier; i++) {
					copiesOfSegment.push(segment);
				}
				return copiesOfSegment;
			});
		});
	}

	GetTriggerPackages() {
		return [
			new TriggerPackage("ConceptLink_NextSequence", this.c.nextSequence_triggerSet, this, {}, async triggerInfo=>{
				this.VoiceNextSequence();
			}),
			new TriggerPackage("ConceptLink_PrevSequence", this.c.prevSequence_triggerSet, this, {}, async triggerInfo=>{
				this.VoicePrevSequence();
			}),
			new TriggerPackage("ConceptLink_PlayAudio", this.c.playAudio_triggerSet, this, {}, async triggerInfo=>{
				this.PlayAudioFileForVisualization();
			}),
			new TriggerPackage("ConceptLink_RandomizeAudio", this.c.randomizeAudio_triggerSet, this, {}, triggerInfo=>{
				this.SelectNewAudioFile();
				this.PlayAudioFileForVisualization();
			}),
		];
	}

	OnStart() {
		this.effectPlayer.effectPointer = this.c.effect2;
		this.effectPlayer.OnStart();
		this.AppendNewEventSequenceAndSetAsCurrent();
		this.SelectNewAudioFile();
	}
	OnStop() {
		this.effectPlayer.Stop();
		this.dbAutorun_disposer();
	}

	async NarrateText(text: string, volumeMultiplier = 1, opt?: NarrateText_ForEngineComp_OptInit) {
		return await NarrateText_ForEngineComp(this, text, this.c.volumeMultiplier * volumeMultiplier, this.c.voiceSoundTag, opt);
	}

	// players
	effectPlayer = new EffectPointerPlayer();

	// sequences
	dbAutorun_disposer: ()=>void;
	validSegmentsForSequences_raw: JournalSegment[];
	validSegmentsForSequences_withBoosts: JournalSegment[];
	sequencesSelected = [] as EventSequence[];
	sequencesSelected_currentIndex = -1;
	sequencesSelected_visualized = new Set<number>();

	// audio files
	currentAudio: AudioFileEntry|n;

	// solver
	cycle_voicingsMade = 0; // excludes "repeat" voicings (ie. voicing a previous/already-voiced sequence)
	cycle_visualizationsMade = 0;
	OnLeavePhase_Drifting(newPhase: Phase): void {
		this.cycle_voicingsMade = 0;
		this.cycle_visualizationsMade = 0;
	}

	//lastSequenceNarrate_utteranceId: string|n;
	async NarrateCurrentSequence(volumeMultiplier = 1, opt?: NarrateText_ForEngineComp_OptInit) {
		const sequence = this.sequencesSelected[this.sequencesSelected_currentIndex];
		if (sequence) {
			const text = sequence.items.join("; THEN: ");
			//const utteranceId = NewUtteranceID(text);
			//this.lastSequenceNarrate_utteranceId = utteranceId;
			return await this.NarrateText(text, volumeMultiplier, opt);
		}
	}
	/*CancelLastSequenceNarrate() {
		if (NarrateText_lastUtteranceId == this.lastSequenceNarrate_utteranceId) {
			NarrateText_StopAll();
		}
	}*/

	async VoiceNextSequence(volumeMultiplier = 1, opt?: NarrateText_ForEngineComp_OptInit) {
		//this.sequencesSelected_currentIndex = (this.sequencesSelected_currentIndex + 1).KeepBetween(0, this.sequencesSelected.length - 1);
		this.EnsureVisualizerNotPlaying();

		// if at end of list, generate new sequence and select it; else, select next sequence
		const addNewSequence = this.sequencesSelected_currentIndex == this.sequencesSelected.length - 1;
		if (addNewSequence) {
			this.AppendNewEventSequenceAndSetAsCurrent();
		} else {
			this.sequencesSelected_currentIndex++;
		}

		// only do these effects if voicing a new sequence (going back/forward in existing list would only be if user missed something)
		if (addNewSequence) {
			this.cycle_voicingsMade++;
			if (this.c.solver_enabled) this.TryToSolve(1, 0);
			this.s.AsLocal!.AddEvent({type: "ConceptLink.TargetVoiced"});
		}

		// have rc-cues play first, so that if user makes it to the sequence-voicing part, they can focus fully on it
		let timeToCueRC = false;
		if (this.c.cueRC_chanceAsBuildup) {
			this.cueRC_waitBuildup = (this.cueRC_waitBuildup + this.c.cueRC_chance).RoundTo(this.c.cueRC_chance);
			if (this.cueRC_waitBuildup >= 1) {
				this.cueRC_waitBuildup -= 1;
				timeToCueRC = true;
			}
		} else {
			timeToCueRC = Math.random() < this.c.cueRC_chance;
		}
		if (timeToCueRC) {
			const rcComp = this.s.Comp(RealityCheckComp);
			if (rcComp.behaviorEnabled) {
				await rcComp.PlayCueRCEffect();
			}
		}

		return await this.NarrateCurrentSequence(volumeMultiplier, opt);

		// only do these effects if voicing a new sequence (going back/forward in existing list would only be if user missed something)
		/*if (addNewSequence) {
			this.cycle_voicingsMade++;
			if (this.c.solver_enabled) this.TryToSolve(1, 0);
			this.s.AsLocal!.AddEvent({type: "ConceptLink.TargetVoiced"});
		}*/
	}
	async VoicePrevSequence() {
		this.EnsureVisualizerNotPlaying();
		this.sequencesSelected_currentIndex = (this.sequencesSelected_currentIndex - 1).KeepBetween(0, this.sequencesSelected.length - 1);
		this.cycle_voicingsMade++;
		if (this.c.solver_enabled) this.TryToSolve(1, 0);
		this.NarrateCurrentSequence();
	}
	cueRC_waitBuildup = 0;

	AppendNewEventSequenceAndSetAsCurrent() {
		const onFailed = ()=>{
			throw new Error(`
				Failed to find valid next sequence.
				Do you have enough journal entries?
			`.AsMultiline(0));
		};
		if (this.validSegmentsForSequences_withBoosts.length == 0) onFailed();

		let loopSucceeded = false;
		tryLoop: for (let tryIndex = 0; tryIndex < 10; tryIndex++) {
			const selectedSegment = this.validSegmentsForSequences_withBoosts.Random();
			const selectedSegmentEvents = GetDreamEvents(selectedSegment, this.c.eventMaxWords);

			const newSequence = FindFreshRange(selectedSegmentEvents, this.c.sequenceSize, []);
			if (newSequence == null) continue tryLoop;
			const events = Range(newSequence.first, newSequence.after, 1, false).map(i=>selectedSegmentEvents[i]);
			this.sequencesSelected.push(new EventSequence(events));
			this.sequencesSelected_currentIndex = this.sequencesSelected.length - 1;

			loopSucceeded = true;
			break;
		}
		if (!loopSucceeded) onFailed();
	}

	SelectNewAudioFile() {
		const audioFiles_visualizers = GetAudioFilesWithSubpath(this.c.soundFolder);
		this.currentAudio = audioFiles_visualizers.Random();
	}
	effect2_waitBuildup = 0;
	async PlayAudioFileForVisualization() {
		if (this.sequencesSelected_currentIndex == -1) return;

		// add event for target visualization
		if (!this.sequencesSelected_visualized.has(this.sequencesSelected_currentIndex)) {
			this.s.AsLocal!.AddEvent({type: "ConceptLink.TargetVisualized"});
			this.sequencesSelected_visualized.add(this.sequencesSelected_currentIndex);
			this.cycle_visualizationsMade++;
			if (this.c.solver_enabled) this.TryToSolve(0, 1);
		}

		// play effect 1, only if user has specified an audio file for it; it's not played internally
		// (fine if not; some may prefer simple button-press to notify of internal visualization; also makes testing panel work on PC)
		if (this.currentAudio != null) {
			await PlaySound({entry: this.currentAudio}, {volume: this.c.soundVolume, durationLimit: this.c.soundDurationLimit});
		}

		let timeToPlayEffect2 = false;
		if (this.c.effect2_chanceAsBuildup) {
			this.effect2_waitBuildup = (this.effect2_waitBuildup + this.c.effect2_chance).RoundTo(this.c.effect2_chance);
			if (this.effect2_waitBuildup >= 1) {
				this.effect2_waitBuildup -= 1;
				timeToPlayEffect2 = true;
			}
		} else {
			timeToPlayEffect2 = Math.random() < this.c.effect2_chance;
		}

		if (timeToPlayEffect2) {
			await SleepAsync(1000 * this.c.effect2_delay);
			// todo: find way to avoid playing this effect2, if micro-snooze happened during the wait period
			await this.effectPlayer.Play();
		}
	}
	EnsureVisualizerNotPlaying() {
		PlaySound_StopAll();
	}

	TryToSolve(newVoicingsMade: number, newVisualizationsMade: number) {
		const alarmsComp = this.s.Comp(AlarmsComp);
		const sequencesFulfilled = this.c.solver_requireAudioPlay ? this.cycle_visualizationsMade : this.cycle_voicingsMade;
		if (sequencesFulfilled < this.c.solver_minSequences) {
			if (this.c.solver_markProgressBeforeCompletion) {
				alarmsComp.NotifySolveProgress(this.c.solver_maxTimeForProgress);
			}
			return;
		}

		// If we make it here, then the solving has completed!
		// ==========

		alarmsComp.NotifySolveCompleted();
		this.cycle_voicingsMade = 0;
		this.cycle_visualizationsMade = 0;
	}
}

class EventSequence {
	constructor(items: string[]) {
		this.items = items;
	}
	items: string[];
}